import { formatMoney } from "theme";

// Companion element for snippets/subscription-picker.liquid. Keeps the per-plan
// prices in sync with the selected variant (via the theme's "variant:change"
// form event) and mirrors the frequency <select> into the selling_plan radio,
// which the product form submits natively to /cart/add.js.
class SubscriptionPicker extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    try {
      this._allocations = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || "{}");
    } catch {
      this._allocations = {};
    }

    this._variantId = this.getAttribute("variant-id");
    this._savingsPrefix = this.getAttribute("savings-prefix") || "Save";

    this.addEventListener("change", this._onChange.bind(this), { signal });
    document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChange.bind(this), { signal });

    // Browsers restore <select> values on reload/back-navigation, so adopt each
    // dropdown's current plan before the first paint rather than the markup default.
    this.querySelectorAll("[data-plan-select]").forEach((select) => {
      const radio = select.closest("[data-plan-option]")?.querySelector('input[name="selling_plan"]');

      if (radio) {
        radio.value = select.value;
      }
    });

    this._sync();
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  _onChange(event) {
    if (event.target.matches("[data-plan-select]")) {
      const radio = event.target.closest("[data-plan-option]")?.querySelector('input[name="selling_plan"]');

      if (radio) {
        radio.value = event.target.value;
        radio.checked = true;
      }
    }

    this._sync();
  }

  _onVariantChange(event) {
    if (!event.detail.variant) {
      return;
    }

    this._variantId = String(event.detail.variant.id);
    this._sync();
  }

  _sync() {
    const settings = window.themeVariables?.settings || {};
    const currencyFormat = settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
    const variantInfo = this._allocations[this._variantId];

    this.querySelectorAll("[data-plan-option]").forEach((option) => {
      const radio = option.querySelector('input[name="selling_plan"]');
      option.classList.toggle("is-selected", radio.checked);

      if (!variantInfo) {
        return;
      }

      const price = radio.value === "" ? variantInfo.price : variantInfo.plans[radio.value];

      if (price == null) {
        return;
      }

      const priceElement = option.querySelector("[data-plan-price]");

      if (priceElement) {
        priceElement.innerHTML = formatMoney(price, currencyFormat);
      }

      const percent = variantInfo.price > 0 ? Math.round(((variantInfo.price - price) * 100) / variantInfo.price) : 0;
      const savingsText = `${this._savingsPrefix} ${percent}%`;

      const compareElement = option.querySelector("[data-plan-compare-price]");

      if (compareElement) {
        compareElement.hidden = percent <= 0;

        if (percent > 0) {
          compareElement.innerHTML = formatMoney(variantInfo.price, currencyFormat);
        }
      }

      const savingsElement = option.querySelector("[data-plan-savings]");

      if (savingsElement) {
        savingsElement.hidden = percent <= 0;

        if (percent > 0) {
          savingsElement.textContent = savingsText;
        }
      }

      const savingsBenefit = option.querySelector("[data-plan-savings-benefit]");

      if (savingsBenefit) {
        savingsBenefit.hidden = percent <= 0;

        if (percent > 0) {
          savingsBenefit.querySelector("[data-plan-savings-text]").textContent = savingsText;
        }
      }
    });
  }
}

if (!window.customElements.get("subscription-picker")) {
  window.customElements.define("subscription-picker", SubscriptionPicker);
}